import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Calendar,
  User,
  MessageCircle,
  Clock,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  ArrowLeft,
  Tag,
  BookOpen,
  Eye,
  ThumbsUp,
  Search,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import { BlogData } from "./BlogData";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = BlogData.posts.find((p) => p.id === parseInt(id));
  const [commentData, setCommentData] = useState({
    name: "",
    email: "",
    website: "",
    comment: "",
  });

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
    window.scrollTo(0, 0);
  }, [id]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Post Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The blog post you're looking for doesn't exist.
            </p>
            <Button asChild>
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get related posts (exclude current post)
  const relatedPosts = BlogData.posts
    .filter((p) => p.id !== post.id)
    .slice(0, 3);

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.join(" ");
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post.title;
    const text = post.content[0];

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + "\n\n" + url)}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    // Handle comment submission
    alert("Comment submitted! (This is a demo)");
    setCommentData({ name: "", email: "", website: "", comment: "" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        <div className="container relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-8 group"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Button>

          <div className="max-w-4xl mx-auto">
            <div
              className="flex flex-wrap gap-2 mb-6"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              {post.categories.map((cat, idx) => (
                <span
                  key={idx}
                  className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full"
                >
                  {cat}
                </span>
              ))}
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {post.title}
            </h1>

            <div
              className="flex flex-wrap items-center gap-6 text-muted-foreground mb-8"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span>{post.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{calculateReadingTime(post.content)} min read</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <span>{post.commentCount} comments</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Article */}
            <main className="lg:col-span-2 space-y-8">
              {/* Featured Image */}
              <div data-aos="fade-up" data-aos-delay="100">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-[400px] md:h-[500px] object-cover rounded-xl shadow-lg"
                />
              </div>

              {/* Article Content */}
              <Card
                className="border-border"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <CardContent className="p-8 md:p-12">
                  <div className="prose prose-lg dark:prose-invert max-w-none">
                    {post.content.map((para, index) => {
                      if (para.startsWith("●") || para.startsWith("👉")) {
                        return (
                          <p
                            key={index}
                            className={cn(
                              "mb-4 text-lg",
                              para.startsWith("👉") && "font-semibold text-primary"
                            )}
                          >
                            {para}
                          </p>
                        );
                      }
                      if (para.endsWith(":")) {
                        return (
                          <h3 key={index} className="text-2xl font-bold mt-8 mb-4">
                            {para}
                          </h3>
                        );
                      }
                      return (
                        <p key={index} className="mb-6 text-lg leading-relaxed text-muted-foreground">
                          {para}
                        </p>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Tags and Share */}
              <Card
                className="border-border"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="h-5 w-5 text-primary" />
                        <span className="font-semibold">Tags:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {post.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="text-sm bg-muted text-muted-foreground px-3 py-1 rounded-full hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Share2 className="h-5 w-5 text-primary" />
                        <span className="font-semibold">Share:</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShare("facebook")}
                          className="h-10 w-10 p-0"
                        >
                          <Facebook className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShare("twitter")}
                          className="h-10 w-10 p-0"
                        >
                          <Twitter className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShare("linkedin")}
                          className="h-10 w-10 p-0"
                        >
                          <Linkedin className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShare("email")}
                          className="h-10 w-10 p-0"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Author Card */}
              {post.author && (
                <Card
                  className="border-border"
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      <img
                        src={post.author.image}
                        alt={post.author.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{post.author.name}</h3>
                        {post.author.bio && (
                          <p className="text-muted-foreground mb-4">{post.author.bio}</p>
                        )}
                        {post.author.socialLinks && (
                          <div className="flex gap-3">
                            {post.author.socialLinks.twitter && (
                              <a
                                href={post.author.socialLinks.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                              >
                                <Twitter className="h-5 w-5" />
                              </a>
                            )}
                            {post.author.socialLinks.facebook && (
                              <a
                                href={post.author.socialLinks.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                              >
                                <Facebook className="h-5 w-5" />
                              </a>
                            )}
                            {post.author.socialLinks.instagram && (
                              <a
                                href={post.author.socialLinks.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                              >
                                <Linkedin className="h-5 w-5" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Comments Section */}
              {post.comments && post.comments.length > 0 && (
                <Card
                  className="border-border"
                  data-aos="fade-up"
                  data-aos-delay="500"
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="h-6 w-6 text-primary" />
                      {post.comments.length} Comments
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {post.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="flex gap-4 pb-6 border-b last:border-0 last:pb-0"
                      >
                        <img
                          src={comment.imgSrc}
                          alt={comment.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold">{comment.name}</h4>
                            <span className="text-sm text-muted-foreground">{comment.date}</span>
                          </div>
                          <p className="text-muted-foreground">{comment.text}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Comment Form */}
              <Card
                className="border-border"
                data-aos="fade-up"
                data-aos-delay="600"
              >
                <CardHeader>
                  <CardTitle>Leave a Comment</CardTitle>
                  <CardDescription>
                    Share your thoughts and join the conversation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCommentSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="name"
                          value={commentData.name}
                          onChange={(e) =>
                            setCommentData({ ...commentData, name: e.target.value })
                          }
                          required
                          placeholder="Your Name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="email"
                          type="email"
                          value={commentData.email}
                          onChange={(e) =>
                            setCommentData({ ...commentData, email: e.target.value })
                          }
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="website" className="text-sm font-medium">
                        Website (Optional)
                      </label>
                      <Input
                        id="website"
                        type="url"
                        value={commentData.website}
                        onChange={(e) =>
                          setCommentData({ ...commentData, website: e.target.value })
                        }
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="comment" className="text-sm font-medium">
                        Comment <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="comment"
                        value={commentData.comment}
                        onChange={(e) =>
                          setCommentData({ ...commentData, comment: e.target.value })
                        }
                        required
                        rows={5}
                        className={cn(
                          "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          "disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        )}
                        placeholder="Write your comment here..."
                      />
                    </div>
                    <Button type="submit" size="lg">
                      Post Comment
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </main>

            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              {/* Search Widget */}
              <Card
                className="border-border"
                data-aos="fade-left"
                data-aos-delay="100"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-primary" />
                    Search
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search articles..."
                      className="pl-10"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Recent Posts */}
              <Card
                className="border-border"
                data-aos="fade-left"
                data-aos-delay="200"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Recent Posts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {BlogData.posts
                    .filter((p) => p.id !== post.id)
                    .slice(0, 5)
                    .map((recentPost) => (
                      <Link
                        key={recentPost.id}
                        to={`/blog-details/${recentPost.id}`}
                        className="flex gap-3 group hover:text-primary transition-colors"
                      >
                        <img
                          src={recentPost.image}
                          alt={recentPost.title}
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                            {recentPost.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {recentPost.date}
                          </p>
                        </div>
                      </Link>
                    ))}
                </CardContent>
              </Card>

              {/* Categories */}
              <Card
                className="border-border"
                data-aos="fade-left"
                data-aos-delay="300"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary" />
                    Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[...new Set(BlogData.posts.flatMap((p) => p.categories))].map(
                      (category, index) => (
                        <Link
                          key={index}
                          to="/blog"
                          className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors group"
                        >
                          <span className="group-hover:text-primary transition-colors">
                            {category}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {BlogData.posts.filter((p) => p.categories.includes(category)).length}
                          </span>
                        </Link>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Tags Cloud */}
              <Card
                className="border-border"
                data-aos="fade-left"
                data-aos-delay="400"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5 text-primary" />
                    Popular Tags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(BlogData.posts.flatMap((p) => p.tags))].map((tag, index) => (
                      <Link
                        key={index}
                        to="/blog"
                        className="text-sm bg-muted text-muted-foreground px-3 py-1 rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 md:py-20 bg-muted/30 border-t">
          <div className="container">
            <div
              className="text-center mb-12"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Related <span className="text-primary">Articles</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <Card
                  key={relatedPost.id}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group overflow-hidden"
                >
                  <Link to={`/blog-details/${relatedPost.id}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {relatedPost.categories.slice(0, 1).map((cat, idx) => (
                          <span
                            key={idx}
                            className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                      <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2">
                        {relatedPost.content[0]}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{relatedPost.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{calculateReadingTime(relatedPost.content)} min</span>
                        </div>
                      </div>
                    </CardContent>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetails;
